/**
 * Extensions we are able to rewrite a file name to. Kept deliberately small — it mirrors
 * {@link EncodableImageType}, the set of image types this app is willing to emit.
 *
 * `webp` is absent for the same reason it is absent there: the attachment endpoint rejects it, so
 * there is no way to reach a name we could not also upload.
 */
export type RewritableExtension = 'jpg' | 'png';

/**
 * Replaces a file name's extension, correctly handling names that have no extension at all.
 *
 * The previous HEIC converter did `name.slice(0, -ext.length) + 'jpg'`, which turned a file
 * picked as `photo` (no dot — common when a photo is shared rather than saved) into a file
 * literally named `"jpg"`.
 *
 * @example withExtension('IMG_0001.HEIC', 'jpg') // 'IMG_0001.jpg'
 * @example withExtension('photo', 'jpg')         // 'photo.jpg'
 * @example withExtension('scan.tar.gz', 'png')   // 'scan.tar.png'
 */
export function withExtension(name: string, ext: RewritableExtension): string {
  const dot = name.lastIndexOf('.');

  // `dot < 1` covers both "no dot at all" and dot-files (`.heic` -> `.heic.jpg`), where the
  // leading dot starts the name rather than an extension.
  if (dot < 1) {
    return `${name}.${ext}`;
  }
  return `${name.slice(0, dot)}.${ext}`;
}
